import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Flame, Award, Target, Calendar, Zap, Crown,
  Trophy, Star, Medal, Shield, Sparkles, Lock
} from 'lucide-react';

const API = '/api';

export default function StreaksPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState(null);

  useEffect(() => {
    fetchStreaks();
  }, []);

  const fetchStreaks = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/streaks`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStreakData(response.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const useJoker = async (type) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/streaks/use-joker?streak_type=${type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Joker kullanıldı! 🃏');
      fetchStreaks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Joker kullanılamadı');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const { streaks, badges, available_badges } = streakData || {};

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-orange-50 via-white to-yellow-50">
      {/* Header */}
      <section className="py-8 bg-gradient-to-r from-orange-500 to-yellow-500 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Flame className="h-10 w-10" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">Streak & Rozetler</h1>
          <p className="text-center text-white/90">Tutarlılığınızı takip edin ve rozetler kazanın</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Streak Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Check-in Streak */}
          <Card className="border-orange-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50">
              <CardTitle className="flex items-center">
                <div className="bg-orange-100 p-2 rounded-full mr-3">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
                Check-in Streak
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="text-6xl font-bold text-orange-600">
                    {streaks?.checkin_streak || 0}
                  </div>
                  <Flame className="absolute -top-2 -right-4 h-8 w-8 text-orange-500" />
                </div>
                <p className="text-gray-500 mt-2">gün üst üste</p>
              </div>
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-1">
                  <span>Rekor</span>
                  <span className="font-bold">{streaks?.max_checkin_streak || 0} gün</span>
                </div>
                <Progress 
                  value={((streaks?.checkin_streak || 0) / Math.max(streaks?.max_checkin_streak || 7, 7)) * 100} 
                  className="h-2" 
                />
              </div>
            </CardContent>
          </Card>

          {/* Activity Streak */}
          <Card className="border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <CardTitle className="flex items-center">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                Aktivite Streak
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="text-6xl font-bold text-blue-600">
                    {streaks?.activity_streak || 0}
                  </div>
                  <Zap className="absolute -top-2 -right-4 h-8 w-8 text-blue-500" />
                </div>
                <p className="text-gray-500 mt-2">gün üst üste</p>
              </div>
              <div className="mt-6">
                <div className="flex justify-between text-sm mb-1">
                  <span>Rekor</span>
                  <span className="font-bold">{streaks?.max_activity_streak || 0} gün</span>
                </div>
                <Progress 
                  value={((streaks?.activity_streak || 0) / Math.max(streaks?.max_activity_streak || 7, 7)) * 100} 
                  className="h-2" 
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Jokers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <span className="text-2xl mr-2">🃏</span>
              Joker Hakları
            </CardTitle>
            <CardDescription>
              Ayda 2 joker hakkınız var. Bir günü kaçırdığınızda streak'inizi koruyabilirsiniz.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-12 h-16 rounded-lg flex items-center justify-center text-2xl ${
                      i < (streaks?.jokers_remaining || 0)
                        ? 'bg-gradient-to-b from-yellow-400 to-orange-400 text-white shadow-lg'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    🃏
                  </div>
                ))}
                <span className="ml-3 text-lg font-medium">
                  {streaks?.jokers_remaining || 0} / 2 kaldı
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => useJoker('checkin')}
                  disabled={(streaks?.jokers_remaining || 0) <= 0}
                >
                  Check-in için Kullan
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => useJoker('activity')}
                  disabled={(streaks?.jokers_remaining || 0) <= 0}
                >
                  Aktivite için Kullan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earned Badges */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Trophy className="h-6 w-6 mr-2 text-yellow-600" />
              Kazanılan Rozetler ({badges?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {badges && badges.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {badges.map((badge, idx) => (
                  <div
                    key={idx}
                    className="text-center p-4 bg-gradient-to-b from-yellow-50 to-orange-50 rounded-xl border border-yellow-200"
                  >
                    <div className="text-4xl mb-2">{badge.icon}</div>
                    <div className="font-semibold text-sm">{badge.name}</div>
                    <div className="text-xs text-gray-500">{badge.description}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Medal className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Henüz rozet kazanmadınız</p>
                <p className="text-sm">Streak'lerinizi artırarak rozetler kazanın!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Available Badges */}
        {available_badges && available_badges.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Lock className="h-5 w-5 mr-2 text-gray-400" />
                Açılabilir Rozetler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {available_badges.map((badge, idx) => (
                  <div
                    key={idx}
                    className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200 opacity-60"
                  >
                    <div className="text-4xl mb-2 grayscale">{badge.icon}</div>
                    <div className="font-semibold text-sm text-gray-600">{badge.name}</div>
                    <div className="text-xs text-gray-400">{badge.description}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/daily-checkin')}
          >
            <Calendar className="h-6 w-6 text-orange-600" />
            <span>Check-in Yap</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex flex-col items-center gap-2"
            onClick={() => navigate('/activity')}
          >
            <Zap className="h-6 w-6 text-blue-600" />
            <span>Aktivite Kaydet</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
